// Fase 31 — Sessões de caixa (abertura/fechamento) com initial float, sangria e suprimento.
//
// Modelo:
//   cashDrawerSessions: {
//     id, status: 'open'|'closed',
//     openedAt, openedBy, openedByName, initialFloat,
//     closedAt?, closedBy?, closedByName?, expectedCash?, countedCash?, difference?,
//     totals: { cash, card, pix, voucher, other, total } (calculado no fechamento),
//     movements: [
//       { type: 'sangria'|'suprimento'|'sale_cash'|'sale_card'|'sale_pix'|'sale_other',
//         amount, at, by, byName, note?, commandId? }
//     ],
//     commandsClosedCount: number
//   }
//
// Apenas UMA sessão pode estar 'open' ao mesmo tempo. Movimentos só são
// aceitos numa sessão aberta. Vendas chamam `recordSale()` automaticamente
// dentro de `closeCommand()` em lib/commands.js.

import db from '@/db';
import { logAudit } from '@/lib/audit';

export const MOV_TYPES = {
  SANGRIA: 'sangria',
  SUPRIMENTO: 'suprimento',
  SALE_CASH: 'sale_cash',
  SALE_CARD: 'sale_card',
  SALE_PIX: 'sale_pix',
  SALE_OTHER: 'sale_other',
};

export const PAYMENT_TO_MOV = {
  cash: MOV_TYPES.SALE_CASH,
  dinheiro: MOV_TYPES.SALE_CASH,
  card: MOV_TYPES.SALE_CARD,
  cartao: MOV_TYPES.SALE_CARD,
  credit: MOV_TYPES.SALE_CARD,
  debit: MOV_TYPES.SALE_CARD,
  pix: MOV_TYPES.SALE_PIX,
};

function methodToMovType(method) {
  if (!method) return MOV_TYPES.SALE_OTHER;
  return PAYMENT_TO_MOV[String(method).toLowerCase()] || MOV_TYPES.SALE_OTHER;
}

/**
 * Retorna a sessão atualmente aberta (ou null se nenhuma).
 */
export async function getCurrentSession() {
  const open = await db.cashDrawerSessions.where('status').equals('open').first();
  return open || null;
}

/**
 * Fase 31.2 — Bloqueio opcional: se a config `requireOpenSession` está
 * ligada, exige sessão de caixa aberta para criar comandas novas.
 * Lança erro com `code = 'NO_OPEN_SESSION'` para a UI mostrar CTA dedicado.
 */
export async function assertSessionOpenIfRequired() {
  const cfg = await db.config.get('requireOpenSession');
  if (!cfg?.value) return; // bloqueio desligado
  const open = await getCurrentSession();
  if (open) return;
  const err = new Error('Caixa fechado. Abra o caixa antes de criar comandas novas.');
  err.code = 'NO_OPEN_SESSION';
  throw err;
}

export async function getSessionById(id) {
  return db.cashDrawerSessions.get(id);
}

/**
 * Abre uma nova sessão. Bloqueia se já existe uma aberta.
 */
export async function openSession({ initialFloat, operatorId, operatorName }) {
  const existing = await getCurrentSession();
  if (existing) {
    throw new Error(`Já existe um caixa aberto (sessão #${existing.id})`);
  }
  const init = Number(initialFloat) || 0;
  if (init < 0) throw new Error('Fundo inicial não pode ser negativo');

  const id = await db.cashDrawerSessions.add({
    status: 'open',
    openedAt: new Date().toISOString(),
    openedBy: operatorId || null,
    openedByName: operatorName || null,
    initialFloat: init,
    movements: [],
    commandsClosedCount: 0,
  });

  await logAudit({
    table: 'cashDrawerSessions',
    action: 'create',
    record: { id, initialFloat: init },
    modifications: null,
    overrideSummary: `${operatorName || 'Operador'} abriu o caixa (sessão #${id}) com fundo de R$ ${init.toFixed(2).replace('.', ',')}`,
  });

  return db.cashDrawerSessions.get(id);
}

/**
 * Adiciona um movimento (sangria/suprimento/venda) à sessão aberta atual.
 * Lança erro se não há sessão aberta.
 */
export async function addMovement({ type, amount, note, operatorId, operatorName, commandId }) {
  const session = await getCurrentSession();
  if (!session) throw new Error('Nenhum caixa aberto. Abra o caixa antes.');
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) throw new Error('Valor inválido');
  if (!Object.values(MOV_TYPES).includes(type)) throw new Error('Tipo de movimento inválido');

  const mov = {
    type,
    amount: value,
    at: new Date().toISOString(),
    by: operatorId || null,
    byName: operatorName || null,
    note: note || null,
    commandId: commandId || null,
  };
  const movements = Array.isArray(session.movements) ? [...session.movements, mov] : [mov];
  await db.cashDrawerSessions.update(session.id, { movements });

  // Auditoria apenas em movimentos manuais (sangria/suprimento), não em vendas
  if (type === MOV_TYPES.SANGRIA || type === MOV_TYPES.SUPRIMENTO) {
    const label = type === MOV_TYPES.SANGRIA ? 'Sangria' : 'Suprimento';
    await logAudit({
      table: 'cashDrawerSessions',
      action: 'update',
      record: { id: session.id, type, amount: value },
      modifications: null,
      overrideSummary: `${operatorName || 'Operador'} registrou ${label} de R$ ${value.toFixed(2).replace('.', ',')}${note ? ` — ${note}` : ''}`,
    });
  }

  return db.cashDrawerSessions.get(session.id);
}

/**
 * Helper chamado dentro de closeCommand: registra venda automaticamente
 * na sessão aberta (se houver). Silencioso quando não há sessão.
 */
export async function recordSale({ method, amount, commandId, operatorId, operatorName }) {
  const session = await getCurrentSession();
  if (!session) return null; // sem sessão aberta, não bloqueia mas também não registra
  const type = methodToMovType(method);
  return addMovement({
    type,
    amount,
    note: `Comanda #${commandId}`,
    operatorId,
    operatorName,
    commandId,
  }).then(async (s) => {
    // incrementa commandsClosedCount
    await db.cashDrawerSessions.update(s.id, { commandsClosedCount: (s.commandsClosedCount || 0) + 1 });
    return db.cashDrawerSessions.get(s.id);
  });
}

/**
 * Calcula totais e métricas de uma sessão (sem persistir).
 */
export function summarize(session) {
  if (!session) return null;
  const movements = Array.isArray(session.movements) ? session.movements : [];
  const totals = { cash: 0, card: 0, pix: 0, other: 0, sangria: 0, suprimento: 0 };
  for (const m of movements) {
    const v = Number(m.amount) || 0;
    if (m.type === MOV_TYPES.SALE_CASH) totals.cash += v;
    else if (m.type === MOV_TYPES.SALE_CARD) totals.card += v;
    else if (m.type === MOV_TYPES.SALE_PIX) totals.pix += v;
    else if (m.type === MOV_TYPES.SALE_OTHER) totals.other += v;
    else if (m.type === MOV_TYPES.SANGRIA) totals.sangria += v;
    else if (m.type === MOV_TYPES.SUPRIMENTO) totals.suprimento += v;
  }
  const totalSales = totals.cash + totals.card + totals.pix + totals.other;
  const expectedCash = Number(session.initialFloat || 0) + totals.cash + totals.suprimento - totals.sangria;
  return {
    ...totals,
    totalSales,
    expectedCash,
    initialFloat: Number(session.initialFloat || 0),
    commandsClosedCount: session.commandsClosedCount || 0,
  };
}

/**
 * Fecha a sessão. Recebe `countedCash` digitado pelo operador, calcula a
 * diferença vs expectedCash, persiste totais e marca status='closed'.
 */
export async function closeSession({ countedCash, operatorId, operatorName, note }) {
  const session = await getCurrentSession();
  if (!session) throw new Error('Não há caixa aberto para fechar');
  const counted = Number(countedCash);
  if (!Number.isFinite(counted) || counted < 0) throw new Error('Valor contado inválido');

  const sum = summarize(session);
  const difference = counted - sum.expectedCash;

  const closedAt = new Date().toISOString();
  const totalsForStorage = {
    cash: sum.cash,
    card: sum.card,
    pix: sum.pix,
    other: sum.other,
    total: sum.totalSales,
  };

  await db.cashDrawerSessions.update(session.id, {
    status: 'closed',
    closedAt,
    closedBy: operatorId || null,
    closedByName: operatorName || null,
    countedCash: counted,
    expectedCash: sum.expectedCash,
    difference,
    totals: totalsForStorage,
    closeNote: note || null,
  });

  const diffStr = `R$ ${difference.toFixed(2).replace('.', ',')}`;
  const summaryStr = difference === 0
    ? `Caixa fechado (sessão #${session.id}) — bate certinho! Total vendido: R$ ${sum.totalSales.toFixed(2).replace('.', ',')}`
    : `Caixa fechado (sessão #${session.id}) — ${difference > 0 ? 'sobra' : 'falta'} de ${diffStr}. Total vendido: R$ ${sum.totalSales.toFixed(2).replace('.', ',')}`;

  await logAudit({
    table: 'cashDrawerSessions',
    action: 'update',
    record: { id: session.id, status: 'closed', difference },
    modifications: null,
    overrideSummary: `${operatorName || 'Operador'} ${summaryStr}`,
  });

  return db.cashDrawerSessions.get(session.id);
}

/**
 * Lista as últimas N sessões (mais recentes primeiro), p/ relatório.
 */
export async function getRecentSessions(limit = 30) {
  const all = await db.cashDrawerSessions.orderBy('openedAt').reverse().limit(limit).toArray();
  return all;
}
